import { isDifficultyTier } from "./difficulty";
import type { ClientMessage, InputFrame } from "./types";

const ROOM_CODE_PATTERN = /^[A-Z2-9]{6}$/;
const CHARACTER_ID_PATTERN = /^[a-z0-9-]{1,32}$/;
export const DISPLAY_NAME_MAX = 12;

export function normalizeRoomCode(value: string): string | null {
  const normalized = value.trim().toUpperCase().replace(/[01]/g, "");
  return ROOM_CODE_PATTERN.test(normalized) ? normalized : null;
}

export function normalizeCharacterId(value: string | null): string {
  return value && CHARACTER_ID_PATTERN.test(value) ? value : "rook";
}

export function normalizeDisplayName(value: string | null | undefined): string | null {
  const normalized = (value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9 _-]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, DISPLAY_NAME_MAX);
  return normalized.length > 0 ? normalized : null;
}

function isInputFrame(value: unknown): value is InputFrame {
  if (!value || typeof value !== "object") return false;
  const input = value as Partial<InputFrame>;
  return (
    (input.x === -1 || input.x === 0 || input.x === 1) &&
    (input.y === -1 || input.y === 0 || input.y === 1) &&
    typeof input.attack === "boolean" &&
    typeof input.jump === "boolean" &&
    typeof input.special === "boolean"
  );
}

export function parseClientMessage(raw: string): ClientMessage | null {
  if (raw.length > 1_024) return null;

  try {
    const message = JSON.parse(raw) as Partial<ClientMessage>;
    if (message.type === "input") {
      if (
        typeof message.seq !== "number" ||
        !Number.isSafeInteger(message.seq) ||
        !isInputFrame(message.input)
      ) {
        return null;
      }
      return { type: "input", seq: message.seq, input: message.input };
    }

    if (message.type === "continue") return { type: "continue" };
    if (message.type === "return") return { type: "return" };
    if (message.type === "ready" && typeof message.ready === "boolean") {
      return { type: "ready", ready: message.ready };
    }
    if (
      message.type === "swap" &&
      typeof message.characterId === "string" &&
      CHARACTER_ID_PATTERN.test(message.characterId)
    ) {
      return { type: "swap", characterId: message.characterId };
    }
    if (message.type === "rename" && typeof message.displayName === "string") {
      return { type: "rename", displayName: normalizeDisplayName(message.displayName) };
    }
    if (message.type === "difficulty" && isDifficultyTier(message.tier)) {
      return { type: "difficulty", tier: message.tier };
    }
  } catch {
    return null;
  }

  return null;
}

export function createRoomCode(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const random = new Uint8Array(6);
  crypto.getRandomValues(random);
  return Array.from(random, (value) => alphabet[value % alphabet.length]).join("");
}
